import { ReadonlyVec3 } from 'gl-matrix';
import {
    ContourGeometricType,
    DicomContour,
} from '~/domain/dicom/dicomstructureset';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { StructureSet } from '../../../domain/patient/structureset';
import { EnvironmentObject } from '../common/EnvironmentObject';
import { RotatedBoundingBox } from '../common/RotatedBoundingBox';
import { ImageObject } from '../image/ImageObject';
import {
    NotifyPropertyChanged,
    NotifyPropertyChangedHelper,
} from '../notifyPropertyChanged';
import { RegionOfInterest } from './RegionOfInterest';

/**
 * {@link StructureSetObject} represents a collection of {@link RegionOfInterest RegionsOfInterest}.\
 * It inherits from {@link EnvironmentObject} so it has a physical appearance in the PatientSpace.
 */
export class StructureSetObject
    extends EnvironmentObject
    implements NotifyPropertyChanged<StructureSetObject>
{
    /**
     * TODO: Is this a good idea? Atleast it makes things easy.
     *
     */
    public readonly parent: StructureSet;

    /**
     * An {@link Array} of {@link RegionOfInterest RegionsOfInterest} stored within this {@link StructureSetObject}.
     */
    private regionsOfInterest: Map<number, RegionOfInterest>;

    private _selectedStructure: RegionOfInterest | undefined = undefined;
    public get selectedStructure(): RegionOfInterest | undefined {
        return this._selectedStructure;
    }

    public set selectedStructure(roi: RegionOfInterest | undefined) {
        if (!roi) {
            this._selectedStructure = undefined;
            return;
        }
        for (const roiSs of this.structures) {
            // verify that roi is in this structure set
            if (roi === roiSs) {
                this._selectedStructure = roi;
                return;
            }
        }
        throw new Error(
            `Attempt to set selectedStructure to ${roi.roiNumber} - ${roi.name}, but it is not part of this StructureSet`
        );
    }

    /**
     * Method to construct {@link StructureSetObject} directly from a {@link StructureSet}-datamodel, which is DICOM datamodel.
     * @param structureSet The DICOM datamodel from which the {@link StructureSetObject} is constructed.
     */
    public constructor(structureSet: StructureSet) {
        super();
        this.parent = structureSet;
        if (!structureSet.image) {
            throw new Error('StructureSetObject must have an image');
        }
        this._grid = structureSet.image.grid;
        this.regionsOfInterest = new Map<number, RegionOfInterest>();
        for (const roi of structureSet) {
            const regionOfInterest = new RegionOfInterest(roi, this._grid);
            this.regionsOfInterest.set(roi.roiNumber, regionOfInterest);
            regionOfInterest.addListener(() =>
                this.notifyPropertyChangedListeners()
            );
        }
    }

    public get displayName(): string {
        return this.parent.structureSetLabel;
    }

    public get sopInstanceUID(): string {
        return this.parent.dicomUid;
    }

    private readonly _notifyPropertyChangedHelper =
        new NotifyPropertyChangedHelper<StructureSetObject>();

    public addListener(listener: (arg0: StructureSetObject) => void): void {
        this._notifyPropertyChangedHelper.addListener(listener);
    }

    public removeListener(listener: (arg0: StructureSetObject) => void): void {
        this._notifyPropertyChangedHelper.removeListener(listener);
    }

    private notifyPropertyChangedListeners(): void {
        this._notifyPropertyChangedHelper.notifyListeners(this);
    }

    public get structures(): RegionOfInterest[] {
        return Array.from(this.regionsOfInterest.values());
    }

    /**
     * Ensure contour edits to WebGL models are updated to the domain and DICOM datamodels.
     */
    public saveChanges(): void {
        const structureSet = this.parent;
        if (structureSet.image) {
            const image3d = structureSet.image;
            for (const roi of this.structures) {
                if (roi.hasBeenModified) {
                    const roiContours: DicomContour[] = [];
                    const contourSequence = roi.sequence;
                    for (const slice of contourSequence.contourSlices) {
                        const imageUid = image3d.getUidForSlice(
                            slice.normalPosition
                        );
                        if (!imageUid) {
                            const point = image3d.grid.center;
                            point[2] = slice.normalPosition;
                            if (
                                image3d.grid
                                    .toBoundingBox()
                                    .isPointInside(point)
                            ) {
                                throw new Error(
                                    `Image UID not found for slice z: ${slice.normalPosition} mm.`
                                );
                            } else {
                                console.warn(
                                    `Contour outside image z: ${slice.normalPosition} mm. Skipping.`
                                );
                            }
                            continue;
                        }
                        for (const contour of slice.contours) {
                            const dicomContour = new DicomContour(
                                ContourGeometricType.CLOSED_PLANAR,
                                contour.vertices,
                                structureSet.dicomStructureSet.referencedSopClassUid,
                                imageUid
                            );
                            roiContours.push(dicomContour);
                        }
                    }
                    roi.dicomParent.contours = roiContours;
                    structureSet.dicomStructureSet.updateContourSequence(
                        roi.roiNumber
                    );
                }
            }
        }
    }

    public clearAllContours(structureName: string): void {
        for (const roi of this.structures) {
            if (roi.name === structureName) {
                const roiModify = roi.startModify('clearAllContours');
                roi.texture?.signedDistanceField?.clearTexture();
                roiModify.finish();
            }
        }
    }

    public clearContoursOnSlice(
        structureName: string,
        planeCoordinates: number,
        imgObject: ImageObject | undefined
    ): void {
        if (!imgObject) {
            throw new Error('ImageObject is undefined');
        }
        for (const roi of this.structures) {
            if (roi.name === structureName) {
                const roiModify = roi.startModify('clearContoursOnSlice');
                const contourSliceZ =
                    roi.getContourSliceZByPlaneCoords(planeCoordinates);
                roi.texture?.signedDistanceField?.clearSlice(contourSliceZ);
                roiModify.finish();
            }
        }
    }

    public keepEveryNthSlice(
        roiNumber: number,
        keepEveryNthSliceValue: number
    ): EventHandlerResult {
        const roi = this.structures.find(
            (structure) => structure.roiNumber === roiNumber
        );
        const roiModify = roi?.startModify('keepEveryNthSlice');
        const texture = roi?.texture;
        const minSlicePatientCoords = texture?.contentBoundingBox.min;
        const maxSlicePatientCoords = texture?.contentBoundingBox.max;
        const signedDistanceField = texture?.signedDistanceField;
        if (
            !texture ||
            !signedDistanceField ||
            !minSlicePatientCoords ||
            !maxSlicePatientCoords
        ) {
            throw new Error('Texture or signedDistanceField is undefined');
        }
        const allSlices = signedDistanceField.getAllSlices();
        const minSdfIndice = texture.patientCoordsToSdfIndices(
            minSlicePatientCoords
        )[2];
        const maxSdfIndice = texture.patientCoordsToSdfIndices(
            maxSlicePatientCoords
        )[2];
        const filteredSlices = allSlices.filter(
            (slice) =>
                slice !== minSdfIndice &&
                slice !== maxSdfIndice &&
                slice % keepEveryNthSliceValue !== 0
        );
        filteredSlices.forEach((contourSliceZ) => {
            signedDistanceField.clearSlice(contourSliceZ);
        });
        roiModify?.finish();
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            new VolumeViewUpdateEvent(
                texture.contentBoundingBox,
                LayerBit.CONTOUR_LAYER_BIT
            )
        );
    }

    public getRoi(roiNumber: number): RegionOfInterest | undefined {
        return this.regionsOfInterest.get(roiNumber);
    }

    // method to find the closest roi to the patientCoords
    public findRoi(
        patientCoords: ReadonlyVec3,
        threshold: number = 30
    ): RegionOfInterest | undefined {
        const rois = Array.from(this.regionsOfInterest.values());
        const visibleRois = rois.filter((roi) => roi.isVisible);
        let minDist = 9999;
        let result: RegionOfInterest | undefined = undefined;
        visibleRois.forEach((roi: RegionOfInterest) => {
            const texture = roi.texture;
            const sdf = texture?.signedDistanceField;
            if (sdf) {
                const sdfCoords =
                    texture.patientCoordsToSdfIndices(patientCoords);
                const dist = sdf.distanceToContour(sdfCoords);

                if (dist < threshold && dist < minDist) {
                    result = roi;
                    minDist = dist;
                }
            }
        });

        return result;
    }

    /**
     * Bounding box of the structure set is the whole image.
     */
    public get boundingBox(): RotatedBoundingBox {
        if (!this._grid) {
            throw new Error('StructureSetObject must have an image');
        }
        const bb = this._grid?.toBoundingBox();
        return new RotatedBoundingBox(bb, this._grid.getOrientation());
    }

    /**
     * Method to erase the array of {@link roi RegionOfInterest}.
     */
    public delete() {
        for (const roi of this.structures) {
            roi.delete();
        }
        this.regionsOfInterest.clear();
    }

    public deleteRoi(roiNumber: number): void {
        const roi = this.getRoi(roiNumber);
        if (roi) {
            roi.delete();
            this.regionsOfInterest.delete(roiNumber);
        }
    }

    public setRoisVisibility(visibility: boolean) {
        for (const roi of this.structures) {
            roi.setVisibilityNoNotification(visibility);
        }
    }
}
